import { Button, Stack } from '@mui/material';

import { AnalyticCard } from '../AnalyticCard/AnalyticCard';
import { useMemo } from 'react';
import { isEmptyArray, isNullOrUndefined, withDefault } from '@vy/utils';
import { useArHealthMetrics } from '../../../../../hooks/requests/use-invoices-overviews/use-invoices-ar-health-metrics';
import { Parallelepipeds } from '@vy/ui-design-system';
import { AnalyticCardEmptyState } from '../../EmptyState/AnalyticCardEmptyState';
import { OverdueList } from './components/OverdueCustomersList';
import { getSelectorAttribute, homePageWidgetsSelectors } from '@vy/app-selectors';
import { GetInvoicesArHealthResponse, OverdueBucketRange } from '@vy/api-definition';
import { Link } from 'react-router-dom';
import { AnalyticMetricV2 } from '../AnalyticMetric/AnalyticMetricV2.tsx';

export const ArHealthAnalyticsCard = () => {
  const { data: arHealthMetrics, isLoading: isArHealthMetricsLoading } = useArHealthMetrics({
    suspense: false,
  });

  const overdueStats = useMemo(() => {
    return getOverdueStats(arHealthMetrics?.overdueBuckets);
  }, [arHealthMetrics]);

  const isNoData = isEmptyArray(overdueStats);

  return (
    <AnalyticCard
      title="AR Health"
      dataVy={homePageWidgetsSelectors.arHealthWidget}
      renderAdditionalHeaderContent={() => (
        <Button
          component={Link}
          to="/reports?reportType=AR+Aging"
          variant="text"
          sx={{
            color: '#0521FF',
          }}
          size="small"
        >
          View Report
        </Button>
      )}
      isLoading={isArHealthMetricsLoading}
    >
      {isNoData ? (
        <AnalyticCardEmptyState
          title="No revenue data yet"
          description="Revenue data will appear once invoices start rolling in."
          icon={<Parallelepipeds />}
        />
      ) : (
        <Stack
          sx={{
            gap: "22px",
            flexWrap: "wrap",
            justifyContent: "space-between"
          }}>
          <AnalyticMetricV2
            value={arHealthMetrics?.avgDso ?? 0}
            valueFontSize={20}
            titleSx={{
              marginBottom: '6px',
            }}
            titleText="DSO"
            formatter={(value) => `${value} Days`}
            dataVy={homePageWidgetsSelectors.arHealthWidgetDso}
          />
          <Stack {...getSelectorAttribute(homePageWidgetsSelectors.arHealthWidgetOverdueList)}>
            <OverdueList overdueStats={overdueStats} />
          </Stack>
        </Stack>
      )}
    </AnalyticCard>
  );
};

function getOverdueStats(overdueBuckets: GetInvoicesArHealthResponse['overdueBuckets'] | undefined) {
  if (isNullOrUndefined(overdueBuckets)) {
    return [];
  }

  const bucketsMap = new Map<OverdueBucketRange, { sum: number; count: number }>();

  overdueBuckets.forEach(({ range, sum, count }) => {
    bucketsMap.set(range, { sum, count });
  });

  const zeroToThirtyDaysOverdue = bucketsMap.get(OverdueBucketRange['0-30']);
  const thirtyToSixtyDaysOverdue = bucketsMap.get(OverdueBucketRange['30-60']);
  const sixtyToNinetyDaysOverdue = bucketsMap.get(OverdueBucketRange['60-90']);
  const ninetyDaysOrMoreOverdue = bucketsMap.get(OverdueBucketRange['90+']);

  const overSixtyDaysOverdue = {
    name: '60+ Days',
    priceValue: withDefault(sixtyToNinetyDaysOverdue?.sum, 0) + withDefault(ninetyDaysOrMoreOverdue?.sum, 0),
    count: withDefault(sixtyToNinetyDaysOverdue?.count, 0) + withDefault(ninetyDaysOrMoreOverdue?.count, 0),
  };

  return [
    {
      name: '0-30 days overdue',
      priceValue: withDefault(zeroToThirtyDaysOverdue?.sum, 0),
      count: withDefault(zeroToThirtyDaysOverdue?.count, 0),
      dataVy: homePageWidgetsSelectors.arHealthWidgetOverdue0to30,
    },
    {
      name: '30-60 days overdue',
      priceValue: withDefault(thirtyToSixtyDaysOverdue?.sum, 0),
      count: withDefault(thirtyToSixtyDaysOverdue?.count, 0),
      dataVy: homePageWidgetsSelectors.arHealthWidgetOverdue30to60,
    },
    {
      ...overSixtyDaysOverdue,
      dataVy: homePageWidgetsSelectors.arHealthWidgetOverdue60Plus,
    },
  ];
}
